/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { snakeCaseNavigationName } from 'bundles/degree-description/utils/utils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

type Props = {
  id: string;
  label: string;
  isActiveItem: boolean;
};

const styles = {
  navItem: (_: Theme, isActive: boolean) => css`
    display: inline-block;
    border-bottom: ${isActive && `3px solid var(--cds-color-blue-700)`};
    padding: 1rem;

    a {
      color: ${isActive ? 'var(--cds-color-blue-700)' : 'var(--cds-color-grey-500)'};
      font-weight: ${isActive ? 700 : 400};
      line-height: 1.875rem;

      :hover {
        color: var(--cds-color-blue-700);
      }

      :focus {
        text-decoration: none;
      }
    }
  `,
};

const CertificateNavItem = ({ id, label, isActiveItem }: Props) => {
  const theme = useTheme();
  const track = useTracker();
  const hash = `#${id}`;

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    track('click_page_tab', {
      pageTab: { name: snakeCaseNavigationName(id) },
    });

    if (history.pushState) {
      event.preventDefault();
      history.pushState(null, '', hash);

      const el = document.querySelector(hash) as HTMLElement;
      const stickyHeaderEl = document.querySelector('.rc-StickyHeader');
      const headerMargin = stickyHeaderEl ? 132 : 60;

      const firstFocusable = el?.querySelector(
        'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;
      firstFocusable?.focus();

      if (el?.offsetTop) {
        const scrollTop = el.offsetTop - headerMargin;

        window.scroll({
          top: scrollTop,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <li data-test="CertificateNavItem" css={styles.navItem(theme, isActiveItem)}>
      <TrackedA
        href={hash}
        onClick={onClick}
        trackingName={`nav_item_${id}`}
        data-active={isActiveItem}
        {...(isActiveItem && { 'aria-current': true })}
        data-test="nav-item"
      >
        {label}
      </TrackedA>
    </li>
  );
};

export default CertificateNavItem;
